import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { IconPlus } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import { Switch } from '@/components/Switch';
import TextArea from '@/components/TextArea';
import { SectionNameConfig } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  addRoleSchema,
  editRoleSchema,
  TAddRoleFormValues,
  TEditRoleFormValues,
} from '@/schema/addRoleSchema';
import {
  createRole,
  updateRole,
  getAllSectionList,
} from '@/service/rolesServices';
import { ISectionData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';

interface IRoleData {
  id: string;
  role: string;
  description: string;
  sections: {
    id: string;
    has_access: boolean;
  }[];
}

interface IAddRoleDrawerProps {
  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: () => void;
  editData?: IRoleData;
  fetchRoles: () => void;
  roleName: string;
  setRoleName: (name: string) => void;
}

const AddRoleDrawer = ({
  setIsOpen,
  onSubmit,
  editData,
  fetchRoles,
  roleName,
  setRoleName,
}: IAddRoleDrawerProps) => {
  const { hasAccess } = useAccess();

  const [isLoading, setIsLoading] = useState(false);
  const [sectionsData, setSectionsData] = useState<ISectionData[]>([]);
  const { formatText } = useFormatText();
  const isEditMode = !!editData;

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<TAddRoleFormValues | TEditRoleFormValues>({
    resolver: zodResolver(isEditMode ? editRoleSchema : addRoleSchema),
    defaultValues: {
      role: '',
      description: '',
      permissions: {},
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      reset({
        role: editData.role,
        description: editData.description,
        permissions:
          editData.sections?.reduce(
            (acc, section) => ({
              ...acc,
              [section.id]: section.has_access,
            }),
            {}
          ) || {},
      });
    } else if (sectionsData.length > 0) {
      // Reset to default values when switching to add mode
      // Set all permissions to false explicitly
      const defaultPermissions = sectionsData.reduce(
        (acc, section) => ({
          ...acc,
          [section.id]: false,
        }),
        {}
      );
      reset({
        role: '',
        description: '',
        permissions: defaultPermissions,
      });
    }
  }, [editData, sectionsData, reset]);

  useEffect(() => {
    getAllSection();
  }, []);

  const permissions = watch('permissions');

  const enabledCount = permissions
    ? Object.values(permissions).filter(Boolean).length
    : 0;

  const handleOnSubmit = async (
    data: TAddRoleFormValues | TEditRoleFormValues
  ) => {
    setIsLoading(true);
    try {
      const payload = {
        ...(!isEditMode && { name: data.role }),
        description: data.description,
        sections: sectionsData.map((section) => ({
          id: section.id,
          has_access: !!data.permissions[section.id],
        })),
      };

      const response = isEditMode
        ? await updateRole(editData.id, payload)
        : await createRole(payload);
      if (response?.status) {
        enqueueSnackbar(response?.message, {
          variant: 'success',
        });
        reset();
        setIsOpen(false);
        onSubmit?.();
        fetchRoles();
        setRoleName('');
      } else {
        enqueueSnackbar(
          response?.message || formatText('common.error.generic'),
          {
            variant: 'error',
          }
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(formatText('common.error.generic'), {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAllSection = async () => {
    setIsLoading(true);
    try {
      const response = await getAllSectionList();
      if (response?.status) {
        setSectionsData(response?.data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasAccess(ERoleAccess.SETTINGS_ROLES)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      className="w-150 max-w-[90vw]"
      isFooter
      isHeader
      heading={
        isEditMode
          ? `${formatText('settings.add-role.edit-title')} ${roleName ?? ''}`
          : formatText('settings.add-role.title')
      }
      position="right"
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        desructive: true,
        onClick: () => {
          setIsOpen(false);
          setRoleName('');
          reset();
        },
      }}
      confirmButtonProps={{
        label: isEditMode
          ? formatText('common.actions.save-changes')
          : formatText('settings.add-role.submit'),
        variant: 'primary',
        icon: isEditMode ? undefined : <IconPlus size={20} />,
        onClick: handleSubmit(handleOnSubmit),
        loading: isLoading,
      }}
    >
      <div className="flex flex-col gap-8 w-full">
        {/* Overview Section */}
        <div>
          <h3 className="text-base font-semibold text-neutral-primary mb-4">
            {formatText('settings.add-role.overview')}
          </h3>
          <div className="flex flex-col gap-6">
            {!isEditMode && (
              <Input
                label={formatText('settings.add-role.role-label')}
                name="role"
                placeholder={formatText('settings.add-role.role-placeholder')}
                register={register}
                error={
                  errors.role?.message
                    ? formatText(errors.role.message)
                    : undefined
                }
                isRequired
              />
            )}

            <TextArea
              label={formatText('settings.add-role.description-label')}
              name="description"
              placeholder={formatText(
                'settings.add-role.description-placeholder'
              )}
              register={register}
              error={
                errors.description?.message
                  ? formatText(errors.description.message)
                  : undefined
              }
              maxLength={250}
              showCount
              rows={4}
            />
          </div>
        </div>

        {/* Permission & Access Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-base font-semibold text-neutral-primary">
              {formatText('settings.add-role.permissions-title')}
            </h3>
            <span className="bg-brand-light-50 text-neutral-primary text-xs font-semibold px-2 py-0.5 rounded-full">
              {enabledCount.toString().padStart(2, '0')}
            </span>
          </div>

          <div className="border border-neutral-light-200 p-4 rounded-2xl flex flex-col gap-4">
            {sectionsData.map((section) => (
              <Controller
                key={section.id}
                name={`permissions.${section.id}`}
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-primary">
                      {formatText(SectionNameConfig[section.section_code])}
                    </span>
                    <Switch
                      id={section.id}
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddRoleDrawer;
